/**
 * How a completion report is read on screen.
 *
 * The API returns criterion **keys** — `web_designation`, `photos` — and not
 * labels, because a backend that shipped display strings would ship them in
 * one language. The mapping from key to translation key is therefore here, and
 * it is exhaustive on purpose: a criterion the server grows and this file does
 * not know about renders as its raw key, which is ugly enough to be noticed in
 * review and harmless enough not to break the screen.
 */

/** Criterion keys, in the order the panel lists them. */
export const CRITERIA = [
  "category",
  "web_designation",
  "required_attributes",
  "photos",
  "description",
  "documents",
] as const;

export type CriterionKey = (typeof CRITERIA)[number];

/** The translation key for a criterion, or the raw key if it is unknown. */
export function criterionLabelKey(criterion: string): string {
  return (CRITERIA as readonly string[]).includes(criterion) ? `criterion.${criterion}` : criterion;
}

/**
 * The tone a completion score is drawn in.
 *
 * Three bands rather than a gradient, for the reason the credit bar gives: the
 * reader is deciding what to work on next, and 61 % against 64 % is not a
 * decision. Publishable is success — the fiche can go out, whatever else is
 * missing — which is why this takes the flag and not only the number.
 */
export function completionTone(
  score: number,
  publishable: boolean,
): "success" | "warning" | "danger" {
  if (publishable) return "success";
  return score >= 50 ? "warning" : "danger";
}
